/**
 * ExamModeToggle — a single on/off button for the Ask Tutor composer, not a
 * selector. The exam it applies is fixed by the student's own Settings-page
 * choice (student_profile.target_exam, StudentSettingsPage.jsx) — this
 * button only flips whether that persona applies to the next reply. Label
 * always reads "<Exam> mode" (e.g. "JEE Main mode"); only the fill (off =
 * outline, on = gold) changes.
 *
 * Uses examPrepTheme.js's fixed Exam Mode palettes (one light, one dark,
 * picked by the theme's `dark` flag) -- the same gold on
 * every student theme, so Exam Mode looks identical whatever theme is on.
 *
 * Renders nothing when the student has no target_exam set — there is
 * nothing to toggle.
 */
import React, { useMemo } from 'react';
import { GraduationCap } from 'lucide-react';
import { getExamModeColors, getExamModeGoldGradient } from '../../examPrepTheme';

export const EXAM_LABELS = { JEE_MAIN: 'JEE Main', JEE_ADVANCED: 'JEE Advanced', NEET: 'NEET', SAT: 'SAT', ITO: 'ITO' };

// First-time "what changed" copy (#4) -- the one line per exam that says
// how this exam's mode differs, matching backend chat_framework's
// per-exam persona + _EXAM_TECHNIQUE.
const EXAM_INTRO_FOCUS = {
  JEE_MAIN: 'Pattern tips, the boards-vs-exam shortcut and answer-format checks for numericals',
  JEE_ADVANCED: 'Which concepts a question combines, and an option-by-option check for multi-correct',
  NEET: 'The exact NCERT line, exceptions to remember, memory hooks and which options to eliminate',
  SAT: 'Evidence from the passage for Reading & Writing, and no-penalty guessing since there’s no negative marking',
  ITO: 'Pacing across easy-to-hard questions and elimination reasoning, with no penalty for a wrong guess',
};

const EXAM_INTRO_LEAD = {
  JEE_MAIN: 'Fastest correct method first, then why it works',
  JEE_ADVANCED: 'The right method first — checked, not just fast',
  NEET: 'NCERT-exact answer first, then why it works',
  SAT: 'The answer first, backed by the exact line in the passage',
  ITO: 'The answer first, then why the other options are wrong',
};

const INTRO_SEEN_KEY = 'desky_exam_mode_intro_seen';

export const hasSeenExamIntro = () => {
  try { return localStorage.getItem(INTRO_SEEN_KEY) === '1'; } catch { return true; }
};

/** Dismissible one-time card explaining what Exam Mode changes. */
export function ExamModeIntro({ targetExam, dark, onDismiss }) {
  if (!EXAM_LABELS[targetExam]) return null;
  const colors = getExamModeColors(dark);
  const label = EXAM_LABELS[targetExam];
  const dismiss = () => {
    try { localStorage.setItem(INTRO_SEEN_KEY, '1'); } catch { /* ignore storage errors */ }
    onDismiss?.();
  };
  const points = [
    EXAM_INTRO_LEAD[targetExam],
    'A target time and the common trap on every answer',
    EXAM_INTRO_FOCUS[targetExam],
    'Warns you when a question touches one of your past mistakes',
  ];
  return (
    <div className="mb-2 rounded-2xl px-3.5 py-3 border" data-testid="tutor-exam-intro"
      style={{ background: colors.goldTint, borderColor: `${colors.gold}55` }}>
      <div className="flex items-center gap-2 mb-1.5">
        <GraduationCap className="w-4 h-4" style={{ color: colors.text }} />
        <span className="text-xs font-semibold" style={{ color: 'var(--st-ink)' }}>{label} mode is on</span>
      </div>
      <ul className="space-y-0.5 mb-2">
        {points.map(p => (
          <li key={p} className="text-xs leading-snug flex gap-1.5" style={{ color: 'var(--st-ink)' }}>
            <span style={{ color: colors.gold }}>•</span>{p}
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs" style={{ color: 'var(--st-mute)' }}>Answers still come only from this lesson. Tap the button again to switch off.</span>
        <button type="button" onClick={dismiss} data-testid="tutor-exam-intro-dismiss"
          className="text-xs font-semibold rounded-full px-3 py-1 flex-shrink-0"
          style={{ background: colors.gold, color: colors.onGold }}>
          Got it
        </button>
      </div>
    </div>
  );
}

export default function ExamModeToggle({
  targetExam,
  dark,
  active,
  onToggle,
  testIdPrefix = 'tutor-exam-mode',
}) {
  if (!targetExam || !EXAM_LABELS[targetExam]) return null;
  const colors = getExamModeColors(dark);
  const goldGradient = getExamModeGoldGradient(dark);
  const label = `${EXAM_LABELS[targetExam]} mode`;

  return (
    <button
      type="button"
      onClick={() => onToggle?.(!active)}
      data-testid={`${testIdPrefix}-toggle`}
      aria-pressed={active}
      title={active ? `${label} — tap to switch off` : `${label} — tap to switch on`}
      className="relative h-7 flex items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold shadow-sm hover:shadow-md active:scale-[0.97] transition"
      style={active ? {
        background: goldGradient,
        color: colors.onGold,
        border: '1px solid transparent',
        boxShadow: `0 0 0 3px ${colors.goldTint}`,
      } : {
        background: 'var(--st-card)',
        color: colors.text,
        border: `1px solid ${colors.gold}55`,
      }}
    >
      <GraduationCap className="w-3.5 h-3.5" />
      <span className="leading-none whitespace-nowrap">{label}</span>
      {/* Twinkles on both states -- off, they hint the pill is a premium
          feature worth tapping; on, they keep it feeling live. */}
      <PillTwinkles active={active} colors={colors} />
    </button>
  );
}

// ─── Sparkle — ambient twinkles on the active pill + a star burst on
// activation. Kept small and gold so it reads as "premium", not "confetti".
// Skipped entirely under prefers-reduced-motion.
if (typeof document !== 'undefined' && !document.getElementById('__deskey_exam_sparkle_kf')) {
  const s = document.createElement('style');
  s.id = '__deskey_exam_sparkle_kf';
  // examStarFly bakes the centering translate(-50%,-50%) into every step
  // (same reason as examModeWave below) and reads each particle's own
  // flight vector from CSS variables set inline.
  s.textContent = '@keyframes examStarTwinkle { '
    + '0%,100%{transform:scale(0.3) rotate(0deg);opacity:0} '
    + '50%{transform:scale(1) rotate(90deg);opacity:1} '
    + '} '
    + '@keyframes examStarFly { '
    + '0%{transform:translate(-50%,-50%) scale(0.2) rotate(0deg);opacity:0} '
    + '18%{opacity:1} '
    + '35%{transform:translate(calc(-50% + var(--dx) * 0.45), calc(-50% + var(--dy) * 0.45)) scale(1) rotate(calc(var(--rot) * 0.4))} '
    + '100%{transform:translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(0.5) rotate(var(--rot));opacity:0} '
    + '}';
  document.head.appendChild(s);
}

// A four-point sparkle, not a five-point "star" -- reads as a glint.
function SparkleStar({ size, color, style, className = '' }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width={size} height={size} className={className} style={style}>
      <path d="M12 0C12.9 7.3 16.7 11.1 24 12C16.7 12.9 12.9 16.7 12 24C11.1 16.7 7.3 12.9 0 12C7.3 11.1 11.1 7.3 12 0Z" fill={color} />
    </svg>
  );
}

const TWINKLE_SPOTS = [
  { top: -6, left: -4, size: 9, delay: '0s' },
  { top: -7, right: -5, size: 7, delay: '0.8s' },
  { bottom: -6, right: 14, size: 6, delay: '1.6s' },
];

function PillTwinkles({ active, colors }) {
  if (prefersReducedMotion()) return null;
  return TWINKLE_SPOTS.map(({ size, delay, ...pos }, i) => (
    <SparkleStar
      key={i}
      size={size}
      // Off, the pill sits on a plain background, so the pale gold would
      // wash out -- use the full gold there.
      color={active ? '#FFE7A3' : colors.gold}
      className="absolute pointer-events-none animate-[examStarTwinkle_2.4s_ease-in-out_infinite]"
      style={{ ...pos, animationDelay: delay, opacity: 0,
               filter: `drop-shadow(0 0 1.5px ${colors.goldDeep})` }}
    />
  ));
}

const BURST_COUNT = 14;

/** Stars flying out of the toggle when Exam Mode turns on. Rendered by the
 *  caller inside the chat panel with the same origin + lifetime as
 *  <ExamModeWave>; keyed per activation so each click gets a fresh burst. */
export function ExamModeSparkBurst({ origin, dark }) {
  const colors = getExamModeColors(dark);
  const burstColors = ['#FFE7A3', colors.gold, '#FFF8E1'];
  // Mostly upward (the toggle sits at the bottom of the panel): angles
  // across the upper half-circle, randomised once per mount.
  const particles = useMemo(() => Array.from({ length: BURST_COUNT }, (_, i) => {
    const angle = Math.PI + Math.random() * Math.PI;
    const distance = 50 + Math.random() * 110;
    return {
      dx: `${Math.round(Math.cos(angle) * distance)}px`,
      dy: `${Math.round(Math.sin(angle) * distance)}px`,
      rot: `${Math.round((Math.random() - 0.5) * 360)}deg`,
      size: 7 + Math.round(Math.random() * 8),
      delay: `${Math.round(Math.random() * 120)}ms`,
      colorIdx: i % 3,
    };
  }), []);

  if (prefersReducedMotion() || !origin) return null;
  return particles.map((p, i) => (
    <SparkleStar
      key={i}
      size={p.size}
      color={burstColors[p.colorIdx]}
      className="absolute pointer-events-none z-40 animate-[examStarFly_850ms_cubic-bezier(0.2,0.7,0.3,1)_forwards]"
      style={{
        left: origin.x, top: origin.y, opacity: 0, animationDelay: p.delay,
        '--dx': p.dx, '--dy': p.dy, '--rot': p.rot,
        filter: `drop-shadow(0 0 2px ${colors.gold})`,
      }}
    />
  ));
}

// ─── Wave — a diffuse gold glow that spreads out from the toggle button's
// own screen position across the whole chat panel on activation. `origin`
// is the button's center, measured by the caller (LearnNowPage) via
// getBoundingClientRect against the panel it's absolutely positioned
// inside — not a guessed corner, so the wave visibly starts at the button
// regardless of layout/width.
//
// Deliberately NOT a shrink-wrapped radial-gradient circle: a small crisp
// disc scaling up reads as "a circle appeared", not "color spreading". A
// large, heavily blurred glow with no hard edge (filter: blur, not just a
// gradient falloff) diffuses instead of showing a shape.
//
// The static translate(-50%,-50%) that centers the glow on `origin` has to
// be baked into every keyframe step: an animation's `transform` fully
// replaces the element's inline `transform` while it runs, so the
// centering would otherwise be lost the instant the animation starts.
// Signature moment for turning Exam Mode on; nothing animates turning it off.
if (typeof document !== 'undefined' && !document.getElementById('__deskey_exam_wave_kf')) {
  const s = document.createElement('style');
  s.id = '__deskey_exam_wave_kf';
  s.textContent = '@keyframes examModeWave { '
    + '0%{transform:translate(-50%,-50%) scale(0.15);opacity:0.5} '
    + '55%{opacity:0.24} '
    + '100%{transform:translate(-50%,-50%) scale(7);opacity:0} '
    + '}';
  document.head.appendChild(s);
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export function ExamModeWave({ origin, dark }) {
  if (prefersReducedMotion() || !origin) return null;
  const colors = getExamModeColors(dark);
  return (
    <div
      aria-hidden="true"
      className="absolute pointer-events-none z-30 animate-[examModeWave_950ms_ease-out_forwards]"
      style={{
        left: origin.x, top: origin.y, width: 180, height: 180, borderRadius: '9999px',
        background: colors.gold,
        filter: 'blur(60px)',
      }}
    />
  );
}
